import { memo } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { AffiliateTeleconsultoriaPanel } from "../affiliate/components/AffiliateTeleconsultoriaPanel";
import { TELECONSULTORIA_AFFILIATIONS_TAB } from "../affiliation/constants";
import { useTeleconsultoriaViewAccess } from "../hooks/useTeleconsultoriaViewAccess";
import { useTeleconsultoriaProfile } from "../profile/TeleconsultoriaProfileContext";
import { ProviderTeleconsultoriaHome } from "../provider/components/ProviderTeleconsultoriaHome";
import { TeleconsultoriaOnboarding } from "./TeleconsultoriaOnboarding";
import { TeleconsultoriaViewTabs } from "./TeleconsultoriaViewTabs";

/**
 * Conteúdo da página de Teleconsultoria, decidido pelo PERFIL da organização
 * e pela ABA (Visão Profissional / Visão Clínica, padrão do Pronto Atendimento):
 * - afiliada: aviso da prestadora + teleconsultores e horários dela (sem
 *   disponibilidade e sem afiliações);
 * - prestadora: disponibilidade, convites e (gestor) afiliadas/afiliações;
 * - nenhum: aviso (o menu já fica oculto; cobre acesso direto pela URL).
 */
export const TeleconsultoriaHome = memo(function TeleconsultoriaHome() {
  const { profile, isLoading, errorMessage } = useTeleconsultoriaProfile();
  const [searchParams] = useSearchParams();
  // Link direto para as afiliações (`?tab=afiliacoes`) abre a visão clínica.
  const preferredView =
    searchParams.get("tab") === TELECONSULTORIA_AFFILIATIONS_TAB ? "clinic" : undefined;
  const { view, setView, hasAnyView, showToggle } = useTeleconsultoriaViewAccess(preferredView);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} aria-label="Carregando teleconsultoria" />
      </Stack>
    );
  }

  if (profile.kind === "none") {
    return (
      <Alert severity={errorMessage ? "error" : "info"}>
        {errorMessage ||
          "Sua clínica não presta nem recebe teleconsultoria. Ative a extensão ou peça a afiliação a uma prestadora."}
      </Alert>
    );
  }

  // Extensão ativa sem relação: só a escolha "Prestar ou Receber" (nada de agenda/convite/marcação).
  if (profile.kind === "unassigned") return <TeleconsultoriaOnboarding />;

  if (!hasAnyView) {
    return (
      <Alert severity="info">
        Você não tem permissão para acessar a Teleconsultoria nesta clínica.
      </Alert>
    );
  }

  return (
    <Stack spacing={2} width="100%">
      {showToggle && <TeleconsultoriaViewTabs value={view} onChange={setView} />}
      {/* Falha ao verificar a afiliação: segue como prestadora (só dados próprios). */}
      {profile.kind === "provider" && errorMessage && (
        <Alert severity="warning">{errorMessage}</Alert>
      )}
      <Box sx={{ width: "100%" }}>
        {profile.kind === "affiliate" ? (
          <AffiliateTeleconsultoriaPanel providerName={profile.providerName} view={view} />
        ) : (
          <ProviderTeleconsultoriaHome view={view} />
        )}
      </Box>
    </Stack>
  );
});
